/**
 * PATH: packages/quote-builder/src/components/QDriveFilePicker.tsx
 * FILE_ID: QB-COMP-QDRIVE-PICKER-001
 * PURPOSE: Shared QDrive attach picker: browse a file list, sort, then pick.
 * WHY: Search-first + refetch-on-every-keystroke hung QA Photo/Attach on
 *      "Loading QDrive files...". QDrive already lists and sorts; this
 *      picker must do the same, not invent a second search product.
 * FLOW: open -> list once -> sort/filter locally -> onSelect(asset).
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { ArrowDown, ArrowUp, FileText, Loader2, Search, X } from 'lucide-react'
import { cn } from '@quotech/ui'
import {
  listQDrivePickerFiles,
  type QDrivePickerFile,
} from '../api/quotesApi'

export type { QDrivePickerFile } from '../api/quotesApi'

export type QDrivePickerSort = 'name-asc' | 'name-desc' | 'date-desc' | 'date-asc'

export interface QDriveFilePickerProps {
  open: boolean
  title: string
  description: string
  acceptExtensions?: string[]
  onClose: () => void
  onSelect: (file: QDrivePickerFile) => void | Promise<void>
  loadFiles?: (query?: string) => Promise<QDrivePickerFile[]>
  /** Opt in to checkbox rows plus an "Attach N" action. Default stays single-pick. */
  multiSelect?: boolean
  onSelectMany?: (files: QDrivePickerFile[]) => void | Promise<void>
}

function extensionOf(filename: string): string {
  return filename.split('.').pop()?.toLowerCase() || ''
}

function formatSize(size?: number | null): string {
  if (!size) return ''
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

function sortFiles(rows: QDrivePickerFile[], sort: QDrivePickerSort): QDrivePickerFile[] {
  const copy = [...rows]
  copy.sort((left, right) => {
    if (sort.startsWith('name')) {
      const delta = left.filename.localeCompare(right.filename, undefined, { sensitivity: 'base' })
      return sort === 'name-asc' ? delta : -delta
    }
    const leftDate = left.uploaded_at || ''
    const rightDate = right.uploaded_at || ''
    const delta = leftDate.localeCompare(rightDate)
    return sort === 'date-asc' ? delta : -delta
  })
  return copy
}

export default function QDriveFilePicker({
  open,
  title,
  description,
  acceptExtensions,
  onClose,
  onSelect,
  loadFiles = listQDrivePickerFiles,
  multiSelect = false,
  onSelectMany,
}: QDriveFilePickerProps) {
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState<QDrivePickerSort>('name-asc')
  const [files, setFiles] = useState<QDrivePickerFile[]>([])
  const [loading, setLoading] = useState(false)
  const [selectingId, setSelectingId] = useState<string | null>(null)
  const [checked, setChecked] = useState<string[]>([])
  const [attaching, setAttaching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Multi-pick needs both a handler and the opt-in; without onSelectMany the
  // checkboxes would collect files nothing consumes.
  const multi = multiSelect && Boolean(onSelectMany)
  const loadFilesRef = useRef(loadFiles)
  loadFilesRef.current = loadFiles

  const allowed = useMemo(
    () => acceptExtensions?.map((ext) => ext.replace(/^\./, '').toLowerCase()).filter(Boolean) ?? [],
    [acceptExtensions],
  )

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setQuery('')
    setLoading(true)
    setChecked([])
    setError(null)
    // List once. Search and sort stay local so ".jpg" never hits /search/.
    void loadFilesRef.current().then(
      (rows) => {
        if (!cancelled) setFiles(rows)
      },
      () => {
        if (!cancelled) setError('Could not load QDrive files.')
      },
    ).finally(() => {
      if (!cancelled) setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [open])

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase()
    let rows = files
    if (allowed.length) {
      rows = rows.filter((file) => allowed.includes(extensionOf(file.filename)))
    }
    if (needle) {
      rows = rows.filter((file) => {
        const hay = `${file.filename} ${file.folder_path || ''}`.toLowerCase()
        return hay.includes(needle)
      })
    }
    return sortFiles(rows, sort)
  }, [files, query, allowed, sort])

  if (!open) return null

  const selectFile = async (file: QDrivePickerFile) => {
    setSelectingId(file.id)
    setError(null)
    try {
      await onSelect(file)
      setQuery('')
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not use the selected QDrive file.')
    } finally {
      setSelectingId(null)
    }
  }

  const toggleChecked = (id: string) => {
    setChecked((current) => (
      current.includes(id) ? current.filter((each) => each !== id) : [...current, id]
    ))
  }

  const attachChecked = async () => {
    if (!onSelectMany || checked.length === 0) return
    // Attach in the order the list shows, not the order they were ticked.
    const picked = visible.filter((file) => checked.includes(file.id))
    setAttaching(true)
    setError(null)
    try {
      await onSelectMany(picked)
      setQuery('')
      setChecked([])
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not attach the selected QDrive files.')
    } finally {
      setAttaching(false)
    }
  }

  const toggleSort = (field: 'name' | 'date') => {
    if (field === 'name') {
      setSort((current) => (current === 'name-asc' ? 'name-desc' : 'name-asc'))
      return
    }
    setSort((current) => (current === 'date-desc' ? 'date-asc' : 'date-desc'))
  }

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[80vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-950">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4 dark:border-slate-800">
          <div>
            <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{title}</h2>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{description}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-5 py-3 dark:border-slate-800">
          <button
            type="button"
            onClick={() => toggleSort('name')}
            aria-pressed={sort.startsWith('name')}
            className={cn(
              'inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-xs',
              sort.startsWith('name')
                ? 'border-primary/40 bg-primary/5 text-foreground'
                : 'border-slate-200 text-slate-500 dark:border-slate-700',
            )}
          >
            Sort: Name
            {sort === 'name-asc' ? <ArrowUp className="h-3 w-3" /> : sort === 'name-desc' ? <ArrowDown className="h-3 w-3" /> : null}
          </button>
          <button
            type="button"
            onClick={() => toggleSort('date')}
            aria-pressed={sort.startsWith('date')}
            className={cn(
              'inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-xs',
              sort.startsWith('date')
                ? 'border-primary/40 bg-primary/5 text-foreground'
                : 'border-slate-200 text-slate-500 dark:border-slate-700',
            )}
          >
            Date
            {sort === 'date-asc' ? <ArrowUp className="h-3 w-3" /> : sort === 'date-desc' ? <ArrowDown className="h-3 w-3" /> : null}
          </button>
          <div className="relative min-w-[10rem] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter this list..."
              className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm outline-none focus:border-primary dark:border-slate-700 dark:bg-slate-900"
            />
          </div>
        </div>

        <div className="min-h-[220px] flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-12 text-sm text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading QDrive files...
            </div>
          ) : visible.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
              <FileText className="h-8 w-8 text-slate-300" />
              <p className="text-sm font-medium text-slate-500">{query ? 'No matching QDrive files' : 'No QDrive files found'}</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {visible.map((file) => (
                <button
                  key={file.id}
                  type="button"
                  onClick={() => (multi ? toggleChecked(file.id) : selectFile(file))}
                  disabled={!!selectingId || attaching}
                  aria-pressed={multi ? checked.includes(file.id) : undefined}
                  data-testid={`qdrive-picker-row-${file.id}`}
                  className={cn(
                    'flex w-full items-center gap-3 px-5 py-3 text-left transition-colors hover:bg-slate-50 disabled:opacity-60 dark:hover:bg-slate-900',
                    (selectingId === file.id || (multi && checked.includes(file.id))) && 'bg-primary/5',
                  )}
                >
                  {multi ? (
                    <input
                      type="checkbox"
                      readOnly
                      tabIndex={-1}
                      checked={checked.includes(file.id)}
                      aria-label={`Select ${file.filename}`}
                      className="h-4 w-4 rounded border-slate-300"
                    />
                  ) : selectingId === file.id ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : <FileText className="h-5 w-5 text-slate-400" />}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-slate-800 dark:text-slate-100">{file.filename}</span>
                    <span className="block truncate text-xs text-slate-400">
                      {[file.folder_path, formatSize(file.file_size)].filter(Boolean).join(' - ') || 'QDrive'}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {error && <div className="border-t border-rose-100 bg-rose-50 px-5 py-3 text-xs text-rose-600 dark:border-rose-900/50 dark:bg-rose-950/30">{error}</div>}

        {multi && (
          <div className="flex items-center justify-between gap-3 border-t border-slate-100 px-5 py-3 dark:border-slate-800">
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {checked.length === 0 ? 'Tick the files to attach' : `${checked.length} selected`}
            </span>
            <button
              type="button"
              onClick={attachChecked}
              disabled={checked.length === 0 || attaching}
              data-testid="qdrive-picker-attach"
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-50"
            >
              {attaching && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {checked.length > 1 ? `Attach ${checked.length}` : 'Attach'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
